import { format, addMinutes, differenceInMinutes, isSameDay, addDays } from 'date-fns';

/**
 * Transportation Coordination Service
 * 
 * Optimizes pickup/dropoff timing, route planning, and family car coordination
 * for seamless transportation management.
 */
class TransportationService {
  constructor() {
    this.routeCache = new Map();
    this.trafficPatterns = new Map();
    this.driverPreferences = new Map();
    this.optimizationHistory = new Map();
  }

  /**
   * Analyze transportation needs and generate optimization recommendations
   */
  async analyzeTransportationNeeds(familyData, weatherContext) {
    const { familyCalendar, family, tasks } = familyData;
    
    // Extract events requiring transportation
    const transportEvents = this.extractTransportationEvents(familyCalendar, tasks);
    
    // Group events by time windows for carpool opportunities
    const carpoolWindows = this.identifyCarpoolOpportunities(transportEvents);
    
    // Analyze route optimizations
    const routeOptimizations = await this.analyzeRouteOptimizations(transportEvents);
    
    // Detect transportation conflicts
    const conflicts = this.detectTransportationConflicts(transportEvents, family);
    
    // Generate driver assignments
    const driverAssignments = this.optimizeDriverAssignments(transportEvents, family);
    
    // Consider weather impact
    const weatherImpact = this.assessWeatherImpact(transportEvents, weatherContext);

    return {
      transportEvents,
      carpoolWindows,
      routeOptimizations,
      conflicts,
      driverAssignments,
      weatherImpact,
      summary: {
        totalTrips: transportEvents.length,
        optimizationOpportunities: carpoolWindows.length + routeOptimizations.length,
        timeSavingsPotential: this.calculateTotalTimeSavings(carpoolWindows, routeOptimizations),
        conflictRisk: this.assessOverallConflictRisk(conflicts)
      },
      recommendations: this.generateTransportationRecommendations(
        carpoolWindows, 
        routeOptimizations, 
        conflicts, 
        weatherImpact
      )
    };
  }

  /**
   * Extract events that require transportation
   */
  extractTransportationEvents(familyCalendar, tasks) {
    const events = [];
    
    // Calendar events requiring transport
    familyCalendar?.forEach(event => {
      if (this.requiresTransportation(event)) {
        events.push({
          ...event,
          type: 'calendar-event',
          transportType: this.determineTransportType(event),
          pickupTime: this.calculatePickupTime(event),
          dropoffTime: this.calculateDropoffTime(event),
          participants: event.participants || [],
          priority: event.priority || 'medium'
        });
      }
    });

    // Task-related transportation
    tasks?.forEach(task => {
      if (task.location && task.location !== 'Home') {
        events.push({
          id: `task-${task.id}`,
          title: `Task: ${task.title}`,
          date: task.due_date,
          time: task.preferred_time || '12:00',
          location: task.location,
          type: 'task-transport',
          transportType: 'one-way',
          participants: [{ id: task.assigned_to, name: task.assigned_to_name }],
          priority: task.priority || 'medium',
          duration: 60
        });
      }
    });

    return this.sortEventsByDateTime(events);
  }

  /**
   * Identify carpool opportunities
   */
  identifyCarpoolOpportunities(transportEvents) {
    const opportunities = [];
    const timeWindows = this.groupEventsByTimeWindow(transportEvents, 30); // 30-minute windows
    
    timeWindows.forEach(window => {
      if (window.events.length > 1) {
        const carpoolAnalysis = this.analyzeCarpoolFeasibility(window.events);
        
        if (carpoolAnalysis.feasible) {
          opportunities.push({
            type: 'carpool',
            timeWindow: window.timeSlot,
            events: window.events,
            participants: carpoolAnalysis.participants,
            driver: carpoolAnalysis.optimalDriver,
            route: carpoolAnalysis.optimizedRoute,
            timeSavings: carpoolAnalysis.timeSavings,
            mileageSavings: carpoolAnalysis.mileageSavings,
            feasibilityScore: carpoolAnalysis.score,
            constraints: carpoolAnalysis.constraints
          });
        }
      }
    });

    return this.rankOpportunitiesByValue(opportunities);
  }

  /**
   * Analyze route optimizations for multi-stop trips
   */
  async analyzeRouteOptimizations(transportEvents) {
    const optimizations = [];
    const drivers = this.identifyDrivers(transportEvents);
    
    // Group events by driver and day
    drivers.forEach(driver => {
      const driverEvents = this.getDriverEvents(transportEvents, driver);
      const eventsByDay = this.groupEventsByDay(driverEvents);
      
      eventsByDay.forEach((dayEvents, date) => {
        if (dayEvents.length > 1) {
          const routeOptimization = this.optimizeRoute(dayEvents, driver);
          
          if (routeOptimization.improvement > 0.1) { // >10% improvement
            optimizations.push({
              type: 'route-optimization',
              date,
              driver,
              events: dayEvents,
              currentRoute: routeOptimization.currentRoute,
              optimizedRoute: routeOptimization.optimizedRoute,
              timeSavings: routeOptimization.timeSavings,
              mileageSavings: routeOptimization.mileageSavings,
              improvement: routeOptimization.improvement,
              confidence: routeOptimization.confidence
            });
          }
        }
      });
    });

    return optimizations;
  }

  /**
   * Detect transportation conflicts
   */
  detectTransportationConflicts(transportEvents, family) {
    const conflicts = [];
    const drivers = this.getAvailableDrivers(family);
    
    // Check for simultaneous transportation needs
    const simultaneousEvents = this.findSimultaneousEvents(transportEvents);
    
    simultaneousEvents.forEach(eventGroup => {
      const requiredDrivers = this.calculateRequiredDrivers(eventGroup);
      const availableDrivers = this.getAvailableDriversForTimeSlot(drivers, eventGroup[0]);
      
      if (requiredDrivers > availableDrivers.length) {
        conflicts.push({
          type: 'driver-shortage',
          severity: 'high',
          timeSlot: {
            date: eventGroup[0].date,
            time: eventGroup[0].time
          },
          events: eventGroup,
          requiredDrivers,
          availableDrivers: availableDrivers.length,
          shortage: requiredDrivers - availableDrivers.length,
          suggestions: this.generateConflictResolutions(eventGroup, availableDrivers)
        });
      }
    });

    // Check for impossible timing (travel time conflicts)
    const timingConflicts = this.detectTimingConflicts(transportEvents);
    conflicts.push(...timingConflicts);

    // Check for vehicle availability
    const vehicleConflicts = this.detectVehicleConflicts(transportEvents, family);
    conflicts.push(...vehicleConflicts);

    return this.prioritizeConflicts(conflicts);
  }

  /**
   * Optimize driver assignments across events
   */
  optimizeDriverAssignments(transportEvents, family) {
    const drivers = this.getAvailableDrivers(family);
    const assignments = new Map();
    
    // Initialize assignments
    drivers.forEach(driver => {
      assignments.set(driver.id, {
        driver,
        events: [],
        totalDriveTime: 0,
        totalMileage: 0,
        efficiency: 0,
        workload: 0
      });
    });

    // Assign events to drivers using optimization algorithm
    const sortedEvents = this.prioritizeEventsForAssignment(transportEvents);
    
    sortedEvents.forEach(event => {
      const optimalDriver = this.findOptimalDriver(event, assignments, drivers);
      
      if (optimalDriver) {
        const assignment = assignments.get(optimalDriver.id);
        assignment.events.push(event);
        assignment.totalDriveTime += this.estimateDriveTime(event);
        assignment.totalMileage += this.estimateMileage(event);
        assignment.workload = this.calculateDriverWorkload(assignment);
        assignment.efficiency = this.calculateDriverEfficiency(assignment);
      }
    });

    return {
      assignments: Array.from(assignments.values()),
      workloadBalance: this.assessWorkloadBalance(assignments),
      optimization: this.assessAssignmentOptimization(assignments)
    };
  }

  /**
   * Assess weather impact on transportation
   */
  assessWeatherImpact(transportEvents, weatherContext) {
    if (!weatherContext) return null;

    const impacts = [];
    const weatherAlerts = [];

    transportEvents.forEach(event => {
      const eventDate = new Date(event.date);
      const weatherForDay = this.getWeatherForDate(weatherContext, eventDate);
      
      if (weatherForDay) {
        const impact = this.calculateWeatherImpact(event, weatherForDay);
        
        if (impact.severity !== 'none') {
          impacts.push({
            event,
            weather: weatherForDay,
            impact,
            recommendations: this.generateWeatherRecommendations(event, weatherForDay, impact)
          });
        }

        // Generate weather alerts
        if (impact.severity === 'high') {
          weatherAlerts.push({
            type: 'weather-alert',
            severity: 'high',
            date: event.date,
            event: event.title,
            condition: weatherForDay.conditions,
            recommendation: impact.primaryRecommendation
          });
        }
      }
    });

    return {
      impacts,
      weatherAlerts,
      summary: {
        eventsAffected: impacts.length,
        highImpactEvents: impacts.filter(i => i.impact.severity === 'high').length,
        recommendedChanges: impacts.filter(i => i.impact.recommendsChange).length
      }
    };
  }

  /**
   * Helper methods for analysis
   */
  requiresTransportation(event) {
    return event.location && 
           event.location !== 'Home' && 
           event.location !== 'Virtual' && 
           event.location !== 'Online';
  }

  determineTransportType(event) {
    // Simple heuristic - would be enhanced with actual event data
    if (event.category === 'school' || event.category === 'work') {
      return 'drop-off-only';
    }
    if (event.duration && event.duration > 180) { // >3 hours
      return 'drop-off-pickup';
    }
    return 'round-trip';
  }

  calculatePickupTime(event) {
    const eventStart = new Date(event.date + ' ' + (event.time || '00:00'));
    const travelTime = this.estimateTravelTimeToLocation(event.location);
    return addMinutes(eventStart, -travelTime - 5); // 5 min buffer
  }

  calculateDropoffTime(event) {
    const eventStart = new Date(event.date + ' ' + (event.time || '00:00'));
    const duration = event.duration || 60;
    const travelTime = this.estimateTravelTimeFromLocation(event.location);
    return addMinutes(eventStart, duration + travelTime + 5); // 5 min buffer
  }

  groupEventsByTimeWindow(events, windowMinutes) {
    const windows = new Map();
    
    events.forEach(event => {
      const eventTime = new Date(event.date + ' ' + (event.time || '00:00'));
      const windowKey = Math.floor(eventTime.getTime() / (windowMinutes * 60 * 1000));
      
      if (!windows.has(windowKey)) {
        windows.set(windowKey, {
          timeSlot: {
            start: new Date(windowKey * windowMinutes * 60 * 1000),
            end: new Date((windowKey + 1) * windowMinutes * 60 * 1000)
          },
          events: []
        });
      }
      
      windows.get(windowKey).events.push(event);
    });

    return Array.from(windows.values()).filter(w => w.events.length > 0);
  }

  analyzeCarpoolFeasibility(events) {
    const participants = new Set();
    const locations = new Set();
    const drivers = [];
    
    events.forEach(event => {
      event.participants?.forEach(p => {
        participants.add(p.id);
        if (p.canDrive || p.isParent) {
          drivers.push(p);
        }
      });
      locations.add(event.location);
    });

    // Check feasibility constraints
    const feasible = drivers.length > 0 && 
                    locations.size <= 3 && // Max 3 stops
                    participants.size <= 6; // Max 6 people

    if (!feasible) {
      return { feasible: false, reason: 'Constraints not met' };
    }

    // Calculate optimization metrics
    const currentTotalTime = events.reduce((sum, e) => sum + this.estimateDriveTime(e), 0);
    const optimizedRoute = this.optimizeCarpoolRoute(events);
    const optimizedTime = this.calculateRouteTime(optimizedRoute);
    
    return {
      feasible: true,
      participants: Array.from(participants),
      optimalDriver: drivers[0], // Simple selection - could be enhanced
      optimizedRoute,
      timeSavings: currentTotalTime - optimizedTime,
      mileageSavings: this.calculateMileageSavings(events, optimizedRoute),
      score: this.calculateCarpoolScore(events, optimizedRoute),
      constraints: this.identifyConstraints(events)
    };
  }

  optimizeRoute(events, driver) {
    const currentRoute = this.generateCurrentRoute(events);
    const optimizedRoute = this.generateOptimizedRoute(events);
    
    const currentTime = this.calculateRouteTime(currentRoute);
    const optimizedTime = this.calculateRouteTime(optimizedRoute);
    
    return {
      currentRoute,
      optimizedRoute,
      timeSavings: currentTime - optimizedTime,
      mileageSavings: this.calculateMileageSavings(currentRoute, optimizedRoute),
      improvement: (currentTime - optimizedTime) / currentTime,
      confidence: 0.8 // Would be based on data quality
    };
  }

  findSimultaneousEvents(events) {
    const simultaneousGroups = [];
    const processedEvents = new Set();
    
    events.forEach(event => {
      if (processedEvents.has(event.id)) return;
      
      const overlapping = events.filter(other => 
        other.id !== event.id && 
        !processedEvents.has(other.id) &&
        this.eventsOverlapInTime(event, other)
      );
      
      if (overlapping.length > 0) {
        const group = [event, ...overlapping];
        simultaneousGroups.push(group);
        group.forEach(e => processedEvents.add(e.id));
      }
    });
    
    return simultaneousGroups;
  }

  detectTimingConflicts(events) {
    const conflicts = [];
    const sortedEvents = this.sortEventsByDateTime(events);
    
    for (let i = 0; i < sortedEvents.length - 1; i++) {
      const current = sortedEvents[i];
      const next = sortedEvents[i + 1];
      
      if (isSameDay(new Date(current.date), new Date(next.date))) {
        const gap = this.calculateEventGap(current, next);
        const requiredTravelTime = this.estimateTravelTime(current.location, next.location);
        
        if (gap < requiredTravelTime) {
          conflicts.push({
            type: 'timing-conflict',
            severity: 'medium',
            events: [current, next],
            requiredTime: requiredTravelTime,
            availableTime: gap,
            shortfall: requiredTravelTime - gap,
            suggestions: this.generateTimingResolutions(current, next, requiredTravelTime - gap)
          });
        }
      }
    }
    
    return conflicts;
  }

  generateTransportationRecommendations(carpoolWindows, routeOptimizations, conflicts, weatherImpact) {
    const recommendations = [];
    
    // Carpool recommendations
    carpoolWindows.slice(0, 3).forEach(opportunity => {
      recommendations.push({
        type: 'carpool-opportunity',
        priority: opportunity.timeSavings > 30 ? 'high' : 'medium',
        title: `Carpool opportunity saves ${opportunity.timeSavings} minutes`,
        description: `Coordinate ${opportunity.events.length} trips in ${opportunity.timeWindow.start}`,
        action: 'coordinate-carpool',
        timeSlot: opportunity.timeWindow,
        events: opportunity.events,
        timeSavings: opportunity.timeSavings,
        confidence: opportunity.feasibilityScore
      });
    });
    
    // Route optimization recommendations
    routeOptimizations.slice(0, 2).forEach(optimization => {
      recommendations.push({
        type: 'route-optimization',
        priority: 'medium',
        title: `Optimize route to save ${optimization.timeSavings} minutes`,
        description: `Reorder stops for ${optimization.driver.name} on ${format(new Date(optimization.date), 'MMM d')}`,
        action: 'optimize-route',
        date: optimization.date,
        driver: optimization.driver,
        improvement: Math.round(optimization.improvement * 100),
        confidence: optimization.confidence
      });
    });
    
    // Conflict resolution recommendations
    conflicts.filter(c => c.severity === 'high').forEach(conflict => {
      recommendations.push({
        type: 'conflict-resolution',
        priority: 'high',
        title: `Resolve ${conflict.type.replace('-', ' ')}`,
        description: conflict.suggestions?.[0]?.description || 'Review scheduling conflict',
        action: 'resolve-conflict',
        conflict,
        urgency: 'high'
      });
    });
    
    // Weather impact recommendations
    if (weatherImpact?.weatherAlerts) {
      weatherImpact.weatherAlerts.forEach(alert => {
        recommendations.push({
          type: 'weather-alert',
          priority: alert.severity,
          title: `Weather impact: ${alert.condition}`,
          description: alert.recommendation,
          action: 'review-weather-impact',
          date: alert.date,
          event: alert.event,
          confidence: 0.9
        });
      });
    }
    
    return this.prioritizeRecommendations(recommendations);
  }

  /**
   * Utility methods
   */
  sortEventsByDateTime(events) {
    return events.sort((a, b) => {
      const dateA = new Date(a.date + ' ' + (a.time || '00:00'));
      const dateB = new Date(b.date + ' ' + (b.time || '00:00'));
      return dateA - dateB;
    });
  }

  estimateTravelTimeToLocation(location) {
    // Simplified estimation - would integrate with maps API
    const travelTimes = {
      'School': 15,
      'Work': 25,
      'Shopping Center': 20,
      'Doctor': 30,
      'Friend\'s House': 20
    };
    
    return travelTimes[location] || 20; // Default 20 minutes
  }

  estimateTravelTimeFromLocation(location) {
    return this.estimateTravelTimeToLocation(location); // Assume symmetric for now
  }

  estimateTravelTime(fromLocation, toLocation) {
    if (fromLocation === toLocation) return 0;
    if (fromLocation === 'Home') return this.estimateTravelTimeToLocation(toLocation);
    if (toLocation === 'Home') return this.estimateTravelTimeFromLocation(fromLocation);
    
    // Simplified inter-location travel time
    return 25;
  }

  eventsOverlapInTime(event1, event2) {
    const start1 = new Date(event1.date + ' ' + (event1.time || '00:00'));
    const end1 = addMinutes(start1, event1.duration || 60);
    const start2 = new Date(event2.date + ' ' + (event2.time || '00:00'));
    const end2 = addMinutes(start2, event2.duration || 60);
    
    return start1 < end2 && end1 > start2;
  }

  calculateEventGap(event1, event2) {
    const end1 = addMinutes(
      new Date(event1.date + ' ' + (event1.time || '00:00')),
      event1.duration || 60
    );
    const start2 = new Date(event2.date + ' ' + (event2.time || '00:00'));
    return differenceInMinutes(start2, end1);
  }

  getAvailableDrivers(family) {
    const drivers = [];
    
    // Parents are automatically drivers
    family?.parents?.forEach(parent => {
      drivers.push({
        ...parent,
        isDriver: true,
        drivingExperience: 'high',
        availability: 'flexible'
      });
    });
    
    // Kids who can drive
    family?.kids?.forEach(kid => {
      if (kid.canDrive) {
        drivers.push({
          ...kid,
          isDriver: true,
          drivingExperience: 'moderate',
          availability: 'limited'
        });
      }
    });
    
    return drivers;
  }

  calculateTotalTimeSavings(carpoolWindows, routeOptimizations) {
    const carpoolSavings = carpoolWindows.reduce((sum, c) => sum + (c.timeSavings || 0), 0);
    const routeSavings = routeOptimizations.reduce((sum, r) => sum + (r.timeSavings || 0), 0);
    return carpoolSavings + routeSavings;
  }

  assessOverallConflictRisk(conflicts) {
    if (conflicts.length === 0) return 'low';
    
    const highSeverityConflicts = conflicts.filter(c => c.severity === 'high').length;
    if (highSeverityConflicts > 2) return 'high';
    if (highSeverityConflicts > 0 || conflicts.length > 5) return 'medium';
    return 'low';
  }

  prioritizeRecommendations(recommendations) {
    const priorityOrder = { high: 3, medium: 2, low: 1 };
    return recommendations.sort((a, b) => {
      const priorityDiff = priorityOrder[b.priority] - priorityOrder[a.priority];
      if (priorityDiff !== 0) return priorityDiff;
      return (b.confidence || 0.5) - (a.confidence || 0.5);
    });
  }

  // Additional helper methods would be implemented here...
  optimizeCarpoolRoute(events) { return []; }
  calculateRouteTime(route) { return 0; }
  calculateMileageSavings(route1, route2) { return 0; }
  calculateCarpoolScore(events, route) { return 0.8; }
  identifyConstraints(events) { return []; }
  generateCurrentRoute(events) { return []; }
  generateOptimizedRoute(events) { return []; }
  identifyDrivers(events) { return []; }
  getDriverEvents(events, driver) { return []; }
  groupEventsByDay(events) { return new Map(); }
  calculateRequiredDrivers(events) { return 1; }
  getAvailableDriversForTimeSlot(drivers, event) { return drivers; }
  generateConflictResolutions(events, drivers) { return []; }
  detectVehicleConflicts(events, family) { return []; }
  prioritizeConflicts(conflicts) { return conflicts; }
  prioritizeEventsForAssignment(events) { return events; }
  findOptimalDriver(event, assignments, drivers) { return drivers[0]; }
  estimateDriveTime(event) { return 30; }
  estimateMileage(event) { return 15; }
  calculateDriverWorkload(assignment) { return 0.5; }
  calculateDriverEfficiency(assignment) { return 0.8; }
  assessWorkloadBalance(assignments) { return { balanced: true }; }
  assessAssignmentOptimization(assignments) { return { efficient: true }; }
  getWeatherForDate(weatherContext, date) { return null; }
  calculateWeatherImpact(event, weather) { return { severity: 'none' }; }
  generateWeatherRecommendations(event, weather, impact) { return []; }
  rankOpportunitiesByValue(opportunities) { return opportunities; }
  generateTimingResolutions(event1, event2, shortfall) { return []; }
}

// Export singleton instance
export const transportationService = new TransportationService();
export default transportationService;